import * as React from 'react'
import { Browser, Cube, Gear, Pulse, Tag } from '@phosphor-icons/react'
import { cn } from '../../lib/utils'
import { type BadgeColor } from './badge'
import { activityTypeConfig } from './activity-icons'
import { accentText, softSurface } from './badge-colors'

/**
 * EntityGlyph — the canonical soft-square chip for a CRM *record type*: the
 * leading icon on navigation surfaces (pinned tiles, "Recently visited",
 * "Upcoming", global search, breadcrumbs). Where {@link ActivityIcon} is a
 * ringed *circle* for an event *feed* (what happened), EntityGlyph is a filled
 * soft *square* for a *record* you can open (what it is).
 *
 *   <EntityGlyph type="deal" />
 *   <EntityGlyph type="product" size="sm" />
 *
 * **Icons** are shared with the activity feed (`activity-icons.tsx`) so a deal
 * is the same Handshake everywhere; `variant` reuses the Tree's `Cube` (products
 * → variants), `page` a neutral `Browser`.
 *
 * **Colors are a deliberate semantic system** — related record types share a
 * hue family so the palette reads as one system, not 12 arbitrary tints:
 *  - **Commerce (greens)** — the sell-side objects, deepest→lightest green:
 *    `deal` (basil) · `product` (emerald) · `variant` (teal) · `sample` (lime).
 *  - **Money** — `rebate` (amber).
 *  - **People & orgs** — `contact` (violet) · `company` (indigo).
 *  - **Outreach** — `campaign` (pink).
 *  - **Content** — `note` (blue) · `document` (sky).
 *  - **Neutral / time** — `page` · `settings` · `activity` (gray) · `reminder` (orange).
 *  - **Pages that mirror a section** reuse that section's hue: `brands`
 *    (emerald, like `product`). (`settings`/`activity` stay neutral gray.)
 */

export type EntityType =
  | 'contact'
  | 'company'
  | 'deal'
  | 'product'
  | 'variant'
  | 'sample'
  | 'brands'
  | 'rebate'
  | 'campaign'
  | 'note'
  | 'document'
  | 'page'
  | 'settings'
  | 'activity'
  | 'reminder'

/**
 * A reference to a CRM record — the shared shape behind pinned tiles, recently-
 * visited rows, search results, and the Edit Pins editor. Just enough to render
 * a glyph + name and link to the record.
 */
export interface EntityRef {
  /** Stable unique id. */
  id: string
  /** Record type — drives the glyph and the default type caption. */
  type: EntityType
  /**
   * Overrides the type caption a tile renders (defaults to the type's label).
   * Use to show a bespoke glyph under a broader category — e.g. a pinned *page*
   * keeps its distinct glyph (a cog, a file) but reads "Page", not the borrowed
   * record label.
   */
  typeLabel?: string
  /** Record name. */
  name: string
  /** Link target — the record's own page. */
  href?: string
  /**
   * Preview content shown in a popover on click, for records with no dedicated
   * page yet (a contact, note, document, product, variant, campaign). Pass an
   * {@link EntityPreviewCard} — or a function `(close) => …` to receive a handler
   * that dismisses the popover (wire it to the card's `onClose` for a close ×).
   * When set, surfaces reveal the preview instead of navigating to `href`.
   */
  preview?: React.ReactNode | ((close: () => void) => React.ReactNode)
}

export interface EntityTypeConfig {
  /** The record glyph (a `@phosphor-icons/react` icon). */
  icon: React.ReactNode
  /** Human label — the uppercase type caption + `aria` text. */
  label: string
  /** Chip tint from the badge palette. */
  color: BadgeColor
}

/**
 * Record type → { icon, label, color }. Icons reuse {@link activityTypeConfig}
 * (so glyphs can't drift from the activity feed); colors are set here as a
 * deliberate semantic system grouped into hue families (see the component doc).
 */
export const entityTypeConfig: Record<EntityType, EntityTypeConfig> = {
  // People & orgs (cool)
  contact: { icon: activityTypeConfig.contact.icon, label: 'Contact', color: 'violet' },
  company: { icon: activityTypeConfig.account.icon, label: 'Company', color: 'indigo' },
  // Commerce — the sell-side objects, deepest → lightest green
  deal: { icon: activityTypeConfig.deal.icon, label: 'Deal', color: 'primary' },
  product: { icon: activityTypeConfig.product.icon, label: 'Product', color: 'emerald' },
  variant: { icon: <Cube />, label: 'Variant', color: 'teal' },
  sample: { icon: activityTypeConfig.sample.icon, label: 'Sample', color: 'lime' },
  // Brands — the brand catalog page; emerald ties it to its products.
  brands: { icon: <Tag />, label: 'Brands', color: 'emerald' },
  // Money
  rebate: { icon: activityTypeConfig.rebate.icon, label: 'Rebate', color: 'amber' },
  // Outreach
  campaign: { icon: activityTypeConfig.campaign.icon, label: 'Campaign', color: 'pink' },
  // Content
  note: { icon: activityTypeConfig.note.icon, label: 'Note', color: 'blue' },
  document: { icon: activityTypeConfig.document.icon, label: 'Document', color: 'sky' },
  // Neutral / time
  page: { icon: <Browser />, label: 'Page', color: 'gray' },
  // Settings shares `page`'s neutral gray but carries the cog so a pinned
  // "Settings" reads distinctly from a generic page (matches the left-nav icon).
  settings: { icon: <Gear />, label: 'Settings', color: 'gray' },
  // Activity — the workspace activity feed page; neutral gray + a pulse line.
  activity: { icon: <Pulse />, label: 'Activity', color: 'gray' },
  reminder: { icon: activityTypeConfig.reminder.icon, label: 'Reminder', color: 'orange' },
}

/** Ordered list of the entity types (grouped by family) — for legends and story grids. */
export const ENTITY_TYPES = [
  'contact',
  'company',
  'deal',
  'product',
  'variant',
  'sample',
  'brands',
  'rebate',
  'campaign',
  'note',
  'document',
  'page',
  'settings',
  'activity',
  'reminder',
] as const satisfies readonly EntityType[]

export type EntityGlyphSize = 'xs' | 'sm' | 'md'

/** Chip + glyph dimensions per size. */
const glyphSize: Record<EntityGlyphSize, string> = {
  xs: 'size-7 rounded-md [&>svg]:size-4',
  sm: 'size-8 rounded-md [&>svg]:size-[18px]',
  md: 'size-10 rounded-lg [&>svg]:size-5',
}

export type EntityGlyphTone = 'color' | 'muted'

export interface EntityGlyphProps {
  /** Record type — resolves the icon + color. */
  type?: EntityType
  /** Icon override (wins over `type`'s icon). */
  icon?: React.ReactNode
  /** Tint override (wins over `type`'s color). */
  color?: BadgeColor
  /** Chip size — `md` (40px, default), `sm` (32px), or `xs` (28px, dense rows). */
  size?: EntityGlyphSize
  /**
   * `color` (default) — soft-filled tinted chip. `muted` — a bare gray icon (no
   * chip fill, no color), for low-emphasis lists like "Recently visited" where
   * the colored chips read too loud.
   */
  tone?: EntityGlyphTone
  className?: string
}

/**
 * The record chip — a soft-filled rounded square with a tinted glyph (`tone`
 * `color`), or a bare gray icon (`tone` `muted`). Resolves its icon + color from
 * `type` (via {@link entityTypeConfig}); `icon` / `color` override for one-offs.
 * Decorative (`aria-hidden`): the row/tile around it carries the accessible name.
 */
export const EntityGlyph = ({
  type,
  icon,
  color,
  size = 'md',
  tone = 'color',
  className,
}: EntityGlyphProps) => {
  const cfg = type ? entityTypeConfig[type] : undefined
  const resolvedIcon = icon ?? cfg?.icon
  const resolvedColor = color ?? cfg?.color ?? 'gray'
  return (
    <span
      aria-hidden="true"
      className={cn(
        'flex shrink-0 items-center justify-center',
        glyphSize[size],
        tone === 'muted'
          ? 'text-fbui-gray-400'
          : cn(softSurface[resolvedColor], accentText[resolvedColor]),
        className
      )}
    >
      {resolvedIcon}
    </span>
  )
}
EntityGlyph.displayName = 'EntityGlyph'

export interface EntityRowProps {
  /** Record type — resolves the leading glyph. */
  type?: EntityType
  /** Glyph icon override. */
  icon?: React.ReactNode
  /** Glyph tint override. */
  color?: BadgeColor
  /** Primary line — the record name. */
  title: React.ReactNode
  /** Secondary line under the title (a type caption, a parent company, …). */
  subtitle?: React.ReactNode
  /** Right-aligned trailing metadata — a timestamp, a badge, an icon. */
  meta?: React.ReactNode
  /** Renders the row as a link to the record. */
  href?: string
  /** Click handler — renders the row as a button (ignored when `href` is set). */
  onClick?: () => void
  /** Glyph size (default `md`). */
  size?: EntityGlyphSize
  /** Glyph tone — `color` (default) or `muted` (bare gray icon, low-emphasis). */
  glyphTone?: EntityGlyphTone
  className?: string
}

/**
 * EntityRow — one record as a horizontal row: leading {@link EntityGlyph}, a
 * title (+ optional subtitle), and a right-aligned `meta` slot. The shared row
 * behind the "Recently visited" list and any other flat record list (global
 * search, a picker). Interactive when given `href` or `onClick` (hover fill +
 * focus ring); a plain `<div>` otherwise. Square (not rounded) so it stacks
 * cleanly under a `divide-y` without the divider bowing at the corners.
 */
export const EntityRow = React.forwardRef<HTMLElement, EntityRowProps>(
  (
    { type, icon, color, title, subtitle, meta, href, onClick, size = 'md', glyphTone, className },
    ref
  ) => {
    const body = (
      <>
        <EntityGlyph type={type} icon={icon} color={color} size={size} tone={glyphTone} />
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-sm font-medium text-fbui-gray-800">
            {title}
          </span>
          {subtitle != null && (
            <span className="truncate text-xs text-fbui-gray-500">
              {subtitle}
            </span>
          )}
        </span>
        {meta != null && (
          <span className="shrink-0 text-xs text-fbui-gray-500">{meta}</span>
        )}
      </>
    )
    const base = cn(
      'flex w-full items-center px-2 text-left',
      size === 'xs' ? 'gap-2.5 py-1' : 'gap-[0.75rem]',
      size === 'md' ? 'py-2' : size === 'sm' ? 'py-1.5' : ''
    )
    const interactiveClass =
      'outline-none transition-colors hover:bg-fbui-gray-50 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-fbui-ring-brand'

    if (href != null) {
      return (
        <a
          ref={ref as React.Ref<HTMLAnchorElement>}
          href={href}
          className={cn(base, interactiveClass, className)}
        >
          {body}
        </a>
      )
    }
    if (onClick != null) {
      return (
        <button
          ref={ref as React.Ref<HTMLButtonElement>}
          type="button"
          onClick={onClick}
          className={cn(base, interactiveClass, className)}
        >
          {body}
        </button>
      )
    }
    return (
      <div ref={ref as React.Ref<HTMLDivElement>} className={cn(base, className)}>
        {body}
      </div>
    )
  }
)
EntityRow.displayName = 'EntityRow'
